import { CheckCircle2, Link as LinkIcon, ShieldCheck, Target } from 'lucide-react';
import { Badge } from '../../../ui/common-ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/common-ui/card';
import type { GraphNode } from '../types/graph';

interface PolicyInspectorProps {
  node: GraphNode;
}

export function PolicyInspector({ node }: PolicyInspectorProps) {
  const props = node.props || {};
  const targetTypes = Array.isArray(props.policyTargetTypes) ? props.policyTargetTypes : [];
  const linkedAlerts = Array.isArray(props.linkedAlerts) ? props.linkedAlerts : [];
  const isActive = String(props.policyStatus || '').toUpperCase() === 'ACTIVE';

  return (
    <div className="space-y-4">
      <Card className="border-purple-500/30 shadow-lg">
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-purple-500/15 p-2.5">
              <ShieldCheck className="h-5 w-5 text-purple-500" />
            </div>
            <div className="min-w-0 flex-1">
              <CardTitle className="text-base leading-snug">{node.label}</CardTitle>
              <CardDescription className="mt-1.5">{props.description || 'No description provided'}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Badge className={isActive ? 'bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/20' : 'bg-muted text-muted-foreground hover:bg-muted'}>
              {isActive && <CheckCircle2 className="mr-1 h-3 w-3" />}
              {props.policyStatus || 'Unknown status'}
            </Badge>
            {props.cloudPolicyType && <Badge variant="outline">{props.cloudPolicyType}</Badge>}
            {props.severity && <Badge variant="outline">{props.severity}</Badge>}
            {props.isDefault && <Badge variant="secondary">Default</Badge>}
          </div>
          <div className="rounded-xl border border-border/50 bg-muted/30 p-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <LinkIcon className="h-4 w-4 text-purple-500" />
              Covered entities
            </div>
            <p className="mt-1 text-2xl font-semibold">{Number(props.coverageCount || 0)}</p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/50 shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Target className="h-4 w-4 text-purple-500" />
            Policy Scope
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {targetTypes.length > 0 ? targetTypes.map((target: string) => (
              <Badge key={target} variant="outline" className="font-mono text-[10px]">
                {target}
              </Badge>
            )) : <span className="text-xs text-muted-foreground">No target types configured</span>}
          </div>
          {linkedAlerts.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {linkedAlerts.length} linked alert{linkedAlerts.length === 1 ? '' : 's'}
            </p>
          )}
          <p className="break-all font-mono text-[10px] text-muted-foreground">{props.policyId}</p>
        </CardContent>
      </Card>
    </div>
  );
}
